/**
 * The layout spec schema.
 *
 * TypeBox authored, JSON Schema 2020-12 emitted, per @Codex's decision 0004.
 * The spec is DATA, never code — every safety, determinism and privacy
 * property in Morph depends on that single fact.
 */

import { Type, type Static } from '@sinclair/typebox';
import * as V from './vocabulary.js';

/** A closed string enum that keeps its literal union on the TypeScript side.
 *  Type.Union(...map(Type.Literal)) erases to `never` without a mapped-tuple
 *  cast; Type.Unsafe carries the type through and emits cleaner JSON Schema. */
const enumOf = <const T extends readonly string[]>(vals: T) =>
  Type.Unsafe<T[number]>({ type: 'string', enum: [...vals] });

export const RoleRef = enumOf(V.ROLES);

/** A binding carries DIRECTION (decision 0007). A read-only vocabulary can
 *  render a Notion board and never *be* one — position in a board IS a
 *  property value, and moving a card writes it. */
export const Binding = Type.Object(
  {
    slot: Type.String({ minLength: 1, maxLength: 64 }),
    role: RoleRef,
    direction: Type.Union([Type.Literal('read'), Type.Literal('read-write')]),
    /** Required iff direction is read-write; the runtime refuses undeclared writes. */
    mutation: Type.Optional(enumOf(V.MUTATIONS)),
    /**
     * A COMPUTED column: show a named function of this role's value rather than
     * the value. Coda's formula column, with no formula in it — decision 0004's
     * line, that a spec may name a computation and never contain one.
     *
     * Only ever `read`. A derived value has no inverse — you cannot type `1960`
     * into a Year column and have the store work out which day in 1960 was
     * meant — so a binding that both derives and writes is refused rather than
     * silently dropping the write, which is the shape this repo keeps finding.
     */
    derive: Type.Optional(enumOf(V.DERIVATIONS)),
    /**
     * A LOOKUP: show a value read from the object this binding's `role`
     * REFERENCES, rather than the reference itself. Coda's lookup column — the
     * half of Coda's row that `ACCEPTANCE.md` had nothing at all behind.
     *
     * One word, exactly like `derive` beside it, and that parallel is the whole
     * design. `derive` names a function of this role's value; `lookup` names a
     * role to read on the far side of this role's reference. So `role` is the
     * relation followed and `lookup` is what is taken from it.
     *
     * A first version was `{ via, read }`, mirroring `Query.traverse`. It named
     * the relation TWICE — once in `via` and once in the `role` the schema
     * already requires — which is a pair of fields that can disagree and a
     * validation rule to keep them honest. One name cannot disagree with
     * itself.
     *
     * No depth. A traversal collects a SET and needs a bound; a lookup reads
     * one role across ONE hop. A lookup whose value is itself a lookup is
     * honestly said as a second column, not as a number that quietly changes
     * what the first one means — so `lookup` naming a `ref` role is refused in
     * `validate.ts` rather than flattened.
     *
     * Only ever `read`, and for a sharper reason than `derive`'s: a write here
     * would land on a DIFFERENT OBJECT than the row it appears in. A column
     * that quietly edits its neighbour is worse than one that refuses.
     */
    lookup: Type.Optional(RoleRef),
  },
  { additionalProperties: false },
);

/** `uniqueItems`, because `maxItems: 8` over a six-word vocabulary left room for
 *  a repetition count — a payload, never a meaning: a node is no more selectable
 *  for saying `select` three times. A factory, so the two node kinds that expose
 *  affordances do not share one schema instance. */
const Affordances = () =>
  Type.Optional(Type.Array(enumOf(V.AFFORDANCES), { maxItems: 8, uniqueItems: true }));

export const SortKey = Type.Object(
  { role: RoleRef, direction: Type.Union([Type.Literal('asc'), Type.Literal('desc')]) },
  { additionalProperties: false },
);

export const Predicate = Type.Object(
  {
    role: RoleRef,
    op: enumOf(V.OPERATORS),
    /** An INDEX into `spec.viewState`, never a name and never a literal.
     *
     *  A name was the last free-string channel inside a query, and a query is
     *  the one branch the pool gate copied wholesale. A number cannot carry a
     *  name, a diagnosis, or a customer id — so the leak class disappears
     *  rather than being filtered. */
    valueFrom: Type.Optional(Type.Integer({ minimum: 0, maximum: 7 })),
  },
  { additionalProperties: false },
);

/** Recursive filter tree — and / or / not / leaf. */
export const FilterNode = Type.Recursive(
  (Self) =>
    Type.Union([
      Type.Object({ all: Type.Array(Self, { maxItems: 16 }) }, { additionalProperties: false }),
      Type.Object({ any: Type.Array(Self, { maxItems: 16 }) }, { additionalProperties: false }),
      Type.Object({ not: Self }, { additionalProperties: false }),
      Type.Object({ where: Predicate }, { additionalProperties: false }),
    ]),
  { $id: 'FilterNode' },
);

/** The query sublanguage (decision 0008). Present in all ten Notion view types
 *  and in Obsidian's Bases; not layout, not a module, so it lives in the core. */
export const Query = Type.Object(
  {
    filter: Type.Optional(FilterNode),
    sort: Type.Optional(Type.Array(SortKey, { maxItems: 8 })),
    groupBy: Type.Optional(RoleRef),
    subGroupBy: Type.Optional(RoleRef),
    aggregate: Type.Optional(
      Type.Array(
        Type.Object({ role: RoleRef, fn: enumOf(V.AGGREGATIONS) }, { additionalProperties: false }),
        { maxItems: 8 },
      ),
    ),
    /** Graph traversal belongs to the query layer, not to the force-graph
     *  module. Local-graph-depth-N is a BFS from a seed, not a filter. */
    traverse: Type.Optional(
      Type.Object(
        { via: RoleRef, depth: Type.Integer({ minimum: 1, maximum: 4 }) },
        { additionalProperties: false },
      ),
    ),
  },
  { additionalProperties: false },
);

/** A named, typed control whose value drives queries in several regions —
 *  Coda's sliders and selects. Neither object data nor per-render ephemera. */
export const ViewStateSlot = Type.Object(
  {
    name: Type.String({ minLength: 1, maxLength: 64 }),
    role: RoleRef,
    op: enumOf(V.OPERATORS),
  },
  { additionalProperties: false },
);

/**
 * A PARTITION — which regions split around a control, and what is drawn before
 * anyone touches it.
 *
 * @Codex ruled this into the schema on decision 0020, and the ruling is about
 * where the knowledge lives rather than about what it says. The generator emits
 * region A filtering `role op slot0` and region B wrapping the same predicate in
 * `not`; an unset slot makes both inert, so both regions are unfiltered and the
 * lens paints everything twice.
 *
 * The host CANNOT be the one to work that out. A first version had it compare
 * the regions' effective queries and hold back the one that would repeat, argued
 * as the deduplication `answerSubjectRegionScopes` already performs. @Codex
 * rejected it, and the distinction is exact:
 *
 * > *`answerSubjectRegionScopes` ... caches one store answer for identical scope
 * > keys but still assigns that answer to EVERY DECLARED REGION. It changes the
 * > number of reads, not the structure or meaning of the lens.*
 *
 * Sharing an answer among requesters is not removing a requester — and two
 * regions may ask one question while differing in placement, labelling or item
 * layout. *"Query equality licenses sharing an answer; it does not license
 * suppressing one of the requesters."*
 *
 * So the spec says it outright. `branches` names the partition's regions **by
 * their stable node ids**, never by position — an ordinal would make the
 * meaning depend on a traversal order two layers would have to agree about.
 * `unset.show` names which branch stands alone until the control has a value.
 *
 * **No value is carried, ever.** `valueFrom` is an INDEX into `spec.viewState`,
 * exactly as a predicate's is, so the pool gate sees a small integer and never a
 * threshold. The first paint stays unset and unfiltered as the view-state ruling
 * requires; it simply has a declared single-region presentation rather than an
 * inferred one.
 *
 * ONE partition per spec, matching the form @Codex ruled for. A second is a
 * versioned core change under decision 0010 rather than a field that quietly
 * grows an array — which is what "the closed core may gain a new production only
 * through a versioned core decision and migration" means in practice.
 */
export const Partition = Type.Object(
  {
    /** An INDEX into `spec.viewState`. Bounds-checked in the semantic pass
     *  against the slots actually declared, like `Predicate.valueFrom`. */
    valueFrom: Type.Integer({ minimum: 0, maximum: 7 }),
    /** Node ids of the regions this control splits. At least two, or it is not
     *  a partition — one branch is an ordinary filtered collection. */
    branches: Type.Array(Type.String({ minLength: 1, maxLength: 64 }),
      { minItems: 2, maxItems: 8 }),
    /** What is drawn while the slot holds nothing. */
    unset: Type.Object(
      { show: Type.String({ minLength: 1, maxLength: 64 }) },
      { additionalProperties: false },
    ),
  },
  { additionalProperties: false },
);

/**
 * An invocation — decision 0014's "the spec may NAME an action, never CONTAIN
 * one", made concrete.
 *
 * Three things are carried and nothing else:
 *
 * - `action`, a namespaced name resolved against the open registry. It is the
 *   only free string here, and it is shaped so it cannot hold a sentence.
 * - `effects`, what this button claims it may do, from the closed set. The
 *   registry refuses to expose an action whose descriptor declares an effect
 *   the spec did not — the same two-sided contract `MUTATIONS` gives a write
 *   binding in 0007, and what lets the pool carry the effects while dropping
 *   the tenant-specific name.
 * - `args`, POSITIONAL references to a role or a view-state slot. Positional
 *   because an argument NAME would be a free-form record key, which this file
 *   already learned is exactly as wide a channel as a free-form value. No
 *   literal appears: a value is data, and data resolves at invocation time
 *   through the private binding manifest like everything else.
 */
export const Invocation = Type.Object(
  {
    action: Type.String({ minLength: 3, maxLength: 49, pattern: V.ACTION_NAME.source }),
    effects: Type.Array(enumOf(V.EFFECTS), { minItems: 1, maxItems: 3, uniqueItems: true }),
    args: Type.Optional(Type.Array(
      Type.Object(
        {
          role: Type.Optional(RoleRef),
          /** An INDEX into `spec.viewState`, never a name and never a literal —
           *  the same rule `Predicate.valueFrom` follows, for the same reason. */
          valueFrom: Type.Optional(Type.Integer({ minimum: 0, maximum: 7 })),
        },
        { additionalProperties: false },
      ),
      { maxItems: 4 },
    )),
  },
  { additionalProperties: false },
);

/**
 * Placement — decision 0014, amended by @Codex into an algebra.
 *
 * A discriminated union rather than a bag of optional fields, so an
 * ill-formed combination cannot be expressed at all: `docked` without an edge
 * and `anchored` without an anchor are both unrepresentable here rather than
 * caught later. `inline` is the default and may simply be omitted.
 *
 * Note what is ABSENT and must stay absent: there is no x, y, width, height or
 * z-index in this union. Those are view state — private, per-device, restored
 * on open, never pooled. Leaving them out is what makes the pool boundary
 * structural instead of a filter someone has to remember to run.
 */
export const Placement = Type.Union([
  Type.Object({ mode: Type.Literal('inline') }, { additionalProperties: false }),
  Type.Object(
    {
      mode: Type.Literal('docked'),
      edge: enumOf(V.DOCK_EDGES),
      extent: Type.Optional(enumOf(V.EXTENTS)),
      /** Whether the user MAY collapse it. Whether it is collapsed right now is
       *  view state, and the difference is the whole point of 0014. */
      collapsible: Type.Optional(Type.Boolean()),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      mode: Type.Literal('floating'),
      /** Where it rests before the user has ever dragged it. Once they have,
       *  the stored position wins and this is not consulted again. */
      rest: enumOf(V.ATTACHMENTS),
      extent: Type.Optional(enumOf(V.EXTENTS)),
      collapsible: Type.Optional(Type.Boolean()),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      mode: Type.Literal('anchored'),
      /** The id of another node in this same spec. Refusing a dangling anchor
       *  is a semantic check — the schema cannot see the rest of the tree. */
      anchor: Type.String({ minLength: 1, maxLength: 64 }),
      attachment: enumOf(V.ATTACHMENTS),
      extent: Type.Optional(enumOf(V.EXTENTS)),
      collapsible: Type.Optional(Type.Boolean()),
    },
    { additionalProperties: false },
  ),
]);

/** Capability modules occupy POSITIONS IN A FLOW, not slots (decision 0009).
 *  Apple Notes places a drawing, a table, a scanned PDF and a live math result
 *  at character offsets inside one paragraph — so a module may sit inline, and
 *  may contain core primitives in turn. */
export const Node = Type.Recursive(
  (Self) =>
    Type.Union([
      Type.Object(
        {
          kind: Type.Literal('region'),
          id: Type.String({ minLength: 1, maxLength: 64 }),
          flow: enumOf(V.FLOWS),
          labelRole: Type.Optional(RoleRef),
          placement: Type.Optional(Placement),
          /**
           * What this region IS, when it is something in particular.
           *
           * Only `relations` today: the stack of a single object's declared
           * relations, which the renderer draws as a labelled Connections group
           * of compact rows rather than as an ordinary flow.
           *
           * Declared rather than inferred, and @Codex named exactly why. The
           * renderer had been classifying any region whose children were all
           * `node-slot`s — and a node slot may host a complete child spec, so a
           * row of inline page embeds is a legal region of exactly that shape.
           * It would have had its authored flow overridden to a column and
           * gained a heading reading "Connections" over content that is not a
           * relation list.
           *
           * *"Carry an explicit compiled marker for the generated relation
           * list … do not infer this semantic from child kinds or a magic node
           * id."* A structure is not a purpose: two things can be built the
           * same way and mean different things, and only the thing that built
           * them knows which.
           */
          //
          // Then decision 0055 added the PANES of a whole-workspace look —
          // `explorer`, `editor`, `backlinks` — as the same kind of word:
          // what the region is, declared by the thing that built it.
          purpose: Type.Optional(enumOf(V.REGION_PURPOSES)),
          children: Type.Array(Self, { maxItems: 64 }),
        },
        { additionalProperties: false },
      ),
      Type.Object(
        {
          kind: Type.Literal('collection'),
          id: Type.String({ minLength: 1, maxLength: 64 }),
          flow: enumOf(V.FLOWS),
          placement: Type.Optional(Placement),
          invocation: Type.Optional(Invocation),
          query: Type.Optional(Query),
          /** Which set this collection shows — a ROLE the host resolves, never
           *  an address. Absent means `subject`, which is what every collection
           *  written before this field meant. */
          source: Type.Optional(enumOf(V.SOURCES)),
          /**
           * Polymorphic items: pick an item spec by the object's own shape.
           *
           * `when` NAMES a predicate; it does not carry one. It used to be
           * `Type.String({ maxLength: 64 })` — sixty-four characters of
           * anything, with the validator checking only that a guard was
           * present and never what it said. Nothing evaluated it, which is the
           * only reason that was survivable: the moment a renderer read it, a
           * spec would have been carrying an expression, and decision 0004's
           * line is that a spec may NAME a computation and never contain one.
           *
           * So a guard is one word and a role from the closed set: does this
           * object have a value for that role. There is deliberately no `and`,
           * no `or`, and no `not` — a second predicate is another named word
           * or a decision, never a grammar.
           */
          items: Type.Array(
            Type.Object(
              {
                when: Type.Optional(Type.Object(
                  { has: RoleRef },
                  { additionalProperties: false },
                )),
                spec: Self,
              },
              { additionalProperties: false },
            ),
            { minItems: 1, maxItems: 8 },
          ),
          affordances: Affordances(),
        },
        { additionalProperties: false },
      ),
      Type.Object(
        {
          kind: Type.Literal('card'),
          id: Type.String({ minLength: 1, maxLength: 64 }),
          bindings: Type.Array(Binding, { maxItems: 16 }),
          placement: Type.Optional(Placement),
          /** Required iff the node offers `invoke`; the validator refuses each
           *  without the other, so a button with nothing behind it and an
           *  action with no way to press it are both unrepresentable. */
          invocation: Type.Optional(Invocation),
          children: Type.Optional(Type.Array(Self, { maxItems: 16 })),
          affordances: Affordances(),
        },
        { additionalProperties: false },
      ),
      Type.Object(
        {
          kind: Type.Literal('field'),
          id: Type.String({ minLength: 1, maxLength: 64 }),
          binding: Binding,
        },
        { additionalProperties: false },
      ),
      Type.Object(
        {
          kind: Type.Literal('media'),
          id: Type.String({ minLength: 1, maxLength: 64 }),
          binding: Binding,
          fit: Type.Union([Type.Literal('cover'), Type.Literal('contain')]),
          aspect: Type.Optional(enumOf(V.ASPECTS)),
          /** Per-object optional: absence draws nothing rather than a note.
           *  A shared card template over a ragged pool needs this or every
           *  object advertises what it does not have. */
          optional: Type.Optional(Type.Boolean()),
        },
        { additionalProperties: false },
      ),
      /** Renders another object here — Roam's ((uid)) and {{embed}}. The
       *  layout-level decision is which relation mutations this site offers;
       *  the identity of the target is object-model business and never enters
       *  the spec, because a spec naming a node is not content-free and cannot
       *  be pooled. */
      Type.Object(
        {
          kind: Type.Literal('node-slot'),
          id: Type.String({ minLength: 1, maxLength: 64 }),
          via: RoleRef,
          /**
           * Which relation mutations a person may perform here.
           *
           * This was `editable: boolean` and decision 0016 retires it without a
           * compatibility alias. A boolean cannot express the binding-direction
           * rule that writes are DECLARED: attach and detach are distinct
           * authorities, and the only way a boolean could stand for both is by
           * granting the larger one — so every spec that had ever said
           * `editable: true` would silently acquire the right to delete
           * relations.
           *
           * Omission means read-only, and `mutations: []` means the same thing
           * said out loud. `editable` is refused by name in `validateSpec`,
           * with the replacement in the message.
           */
          mutations: Type.Optional(Type.Array(enumOf(V.RELATION_MUTATIONS), { maxItems: 2 })),
          maxDepth: Type.Integer({ minimum: 1, maximum: 4 }),
          /**
           * The spec a referenced object is drawn WITH.
           *
           * Absent means the renderer's built-in preview — a thumbnail, a name
           * and three metadata rows it chooses itself. That is presentation
           * decided in the renderer, which is the one place this product says
           * it is never decided, and it stays only as the fallback for a spec
           * that does not say.
           *
           * Present means a region can host a complete spec, which is what
           * `canon-notion.md` asks for and what an inline page needs.
           */
          children: Type.Optional(Type.Array(Self, { maxItems: 16 })),
        },
        { additionalProperties: false },
      ),
      Type.Object(
        {
          kind: Type.Literal('capability'),
          id: Type.String({ minLength: 1, maxLength: 64 }),
          /**
           * A module name is an open REGISTRY KEY, not a vocabulary enum. Shape
           * validation proves only the syntax; `validateSpec` resolves the name
           * against the process registry and visibly refuses one that has not
           * been registered. That is what makes this a registry rather than a
           * wider enum: a module becomes valid by being REGISTERED, without its
           * manifest ever entering a spec.
           *
           * BOUNDED, and that bound is not decoration. @Codex's first version
           * of this had the pattern and no `maxLength`, and `pool.ts` adds the
           * name to the pooled genome — so an unbounded author-controlled
           * string reached the taste model. Lowercase letters, digits and
           * hyphens carry a phone number or base-36 text as readily as anything
           * else, which is the argument `vocabulary.ts` already makes for
           * `ASPECTS` being a closed set rather than a `[0-9]+/[0-9]+` pattern.
           * 64 matches the ids either side of it.
           */
          capability: Type.String({
            pattern: '^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$', maxLength: 64,
          }),
          version: Type.String({ pattern: '^[0-9]+\\.[0-9]+\\.[0-9]+$' }),
          placement: Type.Optional(Placement),
          /** Scalars only. `Type.Unknown()` let an allowlisted key hold an
           *  arbitrary nested object, which walked straight into the pool. */
          config: Type.Optional(
            Type.Record(
              Type.String({ pattern: '^[a-zA-Z][a-zA-Z0-9]{0,31}$' }),
              Type.Union([Type.String({ maxLength: 32 }), Type.Integer(), Type.Boolean()]),
            ),
          ),
          /** Named positional roles supplied to the module. A channel is an
           * ordinary Binding, so the same direction/mutation contract applies.
           * Names are checked against the capability manifest semantically. */
          channels: Type.Optional(
            Type.Record(
              Type.String({ pattern: '^[a-z][a-z0-9-]{0,23}$' }),
              Binding,
            ),
          ),
          /** Modules contain core primitives in turn. */
          /** Slot names are validated against the capability's closed set in
           *  the semantic pass — a free-form record KEY is as wide a channel as
           *  a free-form value, and easier to overlook. */
          slots: Type.Optional(
            Type.Record(
              Type.String({ pattern: '^[a-z][a-z0-9-]{0,23}$' }),
              Type.Array(Self, { maxItems: 32 }),
            ),
          ),
        },
        { additionalProperties: false },
      ),
    ]),
  { $id: 'Node' },
);

export const LayoutSpec = Type.Object(
  {
    specVersion: Type.Literal(V.SPEC_VERSION),
    vocabularyVersion: Type.Literal(V.VOCABULARY_VERSION),
    target: Type.Object(
      {
        cardinality: enumOf(V.CARDINALITIES),
        mediaWeight: enumOf(V.MEDIA_WEIGHTS),
        context: enumOf(V.USE_CONTEXTS),
      },
      { additionalProperties: false },
    ),
    density: enumOf(V.DENSITIES),
    typography: enumOf(V.TYPE_SYSTEMS),
    palette: enumOf(V.PALETTES),
    viewState: Type.Optional(Type.Array(ViewStateSlot, { maxItems: 8 })),
    partition: Type.Optional(Partition),
    /**
     * The NAMED APP LOOK this spec was generated under — decision 0055.
     *
     * Provenance, carried by the spec itself so a kept reproduction reopens
     * as what it was: *"the emitted spec persists with the kept reproduction
     * in the hub … changing the implementation of `app.obsidian` cannot
     * silently repaint a kept layout."* A closed word plus the template's own
     * version, never a description of the look — the look is the tree below.
     */
    template: Type.Optional(Type.Object(
      { id: enumOf(V.APP_TEMPLATES), version: Type.String({ minLength: 1, maxLength: 32 }) },
      { additionalProperties: false },
    )),
    root: Node,
    /** Opaque reference to the PRIVATE slot -> property-key map. The map itself
     *  never appears here and never enters the pool. This is the invariant that
     *  keeps the pool safe, written down where it can be enforced. */
    bindingManifestRef: Type.Optional(Type.String({ maxLength: 128 })),
  },
  { additionalProperties: false, $id: 'LayoutSpec' },
);

export type TBinding = Static<typeof Binding>;
export type TPlacement = Static<typeof Placement>;
export type TInvocation = Static<typeof Invocation>;
export type TNode = Static<typeof Node>;
export type TPartition = Static<typeof Partition>;
export type TQuery = Static<typeof Query>;
export type TLayoutSpec = Static<typeof LayoutSpec>;

